import { Link, useLocation } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { clearAuthToken } from "@/lib/api";
import {
  LayoutDashboard,
  MessageSquare,
  Search,
  RefreshCw,
  LogOut,
  Settings,
  Database,
  FlaskConical,
  Ticket,
  DollarSign,
} from "lucide-react";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/tickets", icon: Ticket, label: "Tickets" },
  { to: "/incentives", icon: DollarSign, label: "Incentives" },
  { to: "/conversations", icon: MessageSquare, label: "Conversations" },
  { to: "/vectors", icon: Search, label: "Vector search" },
  { to: "/sync", icon: RefreshCw, label: "Sync management" },
  { to: "/sources", icon: Database, label: "Sources" },
  { to: "/evals", icon: FlaskConical, label: "Evaluations" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const handleLogout = () => {
    clearAuthToken();
    window.location.href = "/login";
  };

  // Handle nav click - clear URL params when navigating to same page (e.g., dismiss ticket detail)
  const handleNavClick = (to: string) => {
    if (location.pathname === to || (to !== "/" && location.pathname.startsWith(to))) {
      // Clear URL params when clicking on current page's nav item
      const url = new URL(window.location.href);
      if (url.search) {
        url.search = "";
        window.history.replaceState({}, "", url.toString());
        // Trigger a re-render by dispatching a custom event
        window.dispatchEvent(new Event("clearTicketSelection"));
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 w-64 border-r bg-card">
        <div className="flex items-center justify-center px-4 pt-4">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="-86 -34 172 64" style={{ height: '32px' }}>
            <defs>
              <style>{`
                .pill-bg {
                  fill: #000000;
                }
                .pill-border {
                  fill: none;
                  stroke: #000000;
                  stroke-width: 3;
                }
                .pill-text {
                  fill: #ffffff;
                  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
                  font-weight: 700;
                  font-size: 32px;
                  letter-spacing: 4;
                }
                .pill-pulse {
                  fill: none;
                  stroke: #00ff7a;
                  stroke-width: 4;
                  stroke-linecap: round;
                  stroke-dasharray: 80 1000;
                  stroke-dashoffset: 0;
                  animation: pillPulse 2.2s ease-in-out infinite;
                }
                @keyframes pillPulse {
                  from { stroke-dashoffset: 0; }
                  to   { stroke-dashoffset: -1080; }
                }
              `}</style>
            </defs>

            {/* black pill */}
            <rect className="pill-bg"
                  x="-84" y="-24"
                  width="168" height="48" rx="24" />

            <rect className="pill-border"
                  x="-84" y="-24"
                  width="168" height="48" rx="24" />

            {/* pulse path exactly following pill outline, start at bottom centre */}
            <path className="pill-pulse"
                  d="
                    M 0 24
                    H 60
                    A 24 24 0 0 0 84 0
                    A 24 24 0 0 0 60 -24
                    H -60
                    A 24 24 0 0 0 -84 0
                    A 24 24 0 0 0 -60 24
                    H 0
                  " />

            {/* OREO text */}
            <text className="pill-text"
                  x="0" y="10"
                  textAnchor="middle">
              OREO
            </text>
          </svg>
        </div>
        <nav className="flex flex-col gap-1 p-4">
          {navItems.map((item) => {
            const isActive = location.pathname === item.to || 
              (item.to !== "/" && location.pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => handleNavClick(item.to)}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t p-4">
          <Button
            variant="ghost"
            className="w-full justify-start gap-3"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="mt-2 px-3">
                  <span className="inline-block text-[10px] text-muted-foreground font-mono bg-muted px-2 py-0.5 rounded-full cursor-default">
                    {__BUILD_COMMIT__}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="font-mono">{__BUILD_BRANCH__}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </aside>

      {/* Main content */}
      <main className="pl-64">
        <div className="min-h-screen p-6">{children}</div>
      </main>
    </div>
  );
}

